import { memo, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import type { LearningResource } from '@didaskill/domain/learning-resources/types';
import type { Upload } from '@didaskill/domain/uploads/types';
import {
  AvatarInline,
  Dialog,
  type DialogProps,
  ExpandableTruncate,
  Typography,
} from '@didaskill/ui/design-system';

import { ResourceDisplay } from '../../resources/display/ResourceDisplay';
import { LearningResourceTag } from '../tag/LearningResourceTag';
import messages from './LearningResourceDialog.messages';
import * as styles from './LearningResourceDialog.styles';

export type LearningResourceDialogProps = Pick<DialogProps, 'onRequestClose' | 'isOpen'> & {
  resource: LearningResource;
  isDownloading?: boolean;
  onDownloadFile?: (file: Upload) => void;
  onPlayVideo?: () => Promise<void>;
  footer?: ReactNode;
};

export const LearningResourceDialogBase = ({
  isOpen,
  onRequestClose,
  resource,
  isDownloading,
  onDownloadFile,
  onPlayVideo,
  footer = null,
}: LearningResourceDialogProps) => {
  return (
    <Dialog
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      size={Dialog.SIZES.MEDIUM}
      withBorder={false}
    >
      <Dialog.Body>
        <Dialog.Title>
          <Typography variant={Typography.VARIANTS.BODY_LARGE_BOLD}>{resource.title}</Typography>
        </Dialog.Title>

        <div css={styles.dialogBody}>
          <ResourceDisplay
            resource={resource}
            onDownloadFile={onDownloadFile}
            isDownloading={isDownloading}
            onPlayVideo={onPlayVideo}
          />

          {resource.presenter && <AvatarInline user={resource.presenter} />}

          <div css={styles.tags}>
            {resource.tags.map((tag) => (
              <LearningResourceTag key={tag} label={tag} />
            ))}
          </div>

          <div css={styles.description}>
            {resource.description && (
              <ExpandableTruncate maxLines={3}>
                <Typography variant={Typography.VARIANTS.BODY_MEDIUM_REGULAR}>
                  <FormattedMessage
                    {...messages.DESCRIPTION}
                    values={{
                      description: resource.description,
                      b: (chunks) => (
                        <Typography variant={Typography.VARIANTS.BODY_MEDIUM_BOLD}>
                          {chunks}
                        </Typography>
                      ),
                    }}
                  />
                </Typography>
              </ExpandableTruncate>
            )}
          </div>

          {footer}
        </div>
      </Dialog.Body>
    </Dialog>
  );
};

LearningResourceDialogBase.displayName = 'LearningResourceDialog';

export const LearningResourceDialog = memo(LearningResourceDialogBase);
